// =============================================================================
// HUB IA — les vues (#346 · T5, + Qualité données #370)
// =============================================================================
// DASHBOARD · AGENTS · CAPACITÉS · ÉVÉNEMENTS · VERDICTS · RÈGLES · MONITORING
// + QUALITÉ DONNÉES (#370) — AID-OPP-02 déclinée, avec sa formule de classement
//
// ⚠️ DEUX VUES PORTENT UNE EXIGENCE PRÉCISE DU CHANTIER
//   • ÉVÉNEMENTS montre les événements SANS ABONNÉ. C'est SMELL-05 rendu
//     visible — précédent mesuré : 155 verdicts, 0 tâche ouverte.
//   • VERDICTS montre la DISTRIBUTION DES CINQ. Un verdict constant à 97 %
//     aurait été détecté par cet indicateur dès la première mesure.
//
// CHARTE — zéro couleur en dur. Tout dérive de var(--oh-*), donc tout bascule
// en clair et en foncé. Les blocs colorés utilisent des PAIRES fond/texte
// (--oh-stat-{c}-bg / --oh-stat-{c}-text) : jamais vert sur vert.
//
// L'écran ne recalcule RIEN : il sert ce que le socle mesure. Une vue qui
// referait le calcul de son côté finirait par diverger du moteur, et c'est
// l'écran qui aurait tort en silence.
// =============================================================================

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, ArrowLeft, Boxes, ClipboardList, Gauge, Layers, ListChecks,
  Radio, RefreshCw, Scale, Sparkles, Zap,
} from "lucide-react";
import { getTenantHeaders } from "../../lib/utils";
import PermissionContext from "../../contexts/PermissionContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Les cinq résultats, dans l'ordre de lecture, avec leur paire fond/texte.
const RESULTATS = [
  { cle: "conforme", label: "Conforme", couleur: "green" },
  { cle: "non_conforme", label: "Non conforme", couleur: "red" },
  { cle: "indetermine", label: "Indéterminé", couleur: "amber" },
  { cle: "non_applicable", label: "Non applicable", couleur: "blue" },
  { cle: "erreur_technique", label: "Erreur technique", couleur: "purple" },
];

const MODES = [
  { cle: "off", label: "Off", aide: "ne s'exécute pas" },
  { cle: "manuel", label: "Manuel", aide: "seulement sur demande explicite" },
  { cle: "auto", label: "Auto", aide: "sur événement" },
  { cle: "planifie", label: "Planifié", aide: "périodique" },
];

const VUES = [
  { id: "dashboard", label: "Dashboard", icon: Gauge },
  { id: "points", label: "Points à vérifier", icon: AlertTriangle },
  { id: "agents", label: "Agents", icon: Boxes },
  { id: "capacites", label: "Capacités", icon: Layers },
  { id: "evenements", label: "Événements", icon: Radio },
  { id: "verdicts", label: "Verdicts", icon: ListChecks },
  { id: "regles", label: "Règles", icon: Scale },
  { id: "monitoring", label: "Monitoring", icon: Activity },
  { id: "qualite", label: "Qualité données", icon: ClipboardList },
];

const fond = (c) => `var(--oh-stat-${c}-bg)`;
const texte = (c) => `var(--oh-stat-${c}-text)`;
const bordure = (c) => `var(--oh-stat-${c}-border)`;

async function appeler(chemin, options = {}) {
  const res = await fetch(`${API}/api/hub-ia${chemin}`, {
    headers: { ...getTenantHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    ...options,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} sur ${chemin}`);
  return res.json();
}

// =============================================================================
// Briques d'affichage
// =============================================================================

function Carte({ children, style }) {
  return (
    <div
      style={{
        background: "var(--oh-bg-elevated)",
        border: "0.5px solid var(--oh-border)",
        borderRadius: 12,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Titre({ children, aide }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>{children}</div>
      {aide && (
        <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>{aide}</div>
      )}
    </div>
  );
}

function Encart({ couleur = "blue", titre, children }) {
  return (
    <div
      style={{
        background: fond(couleur),
        color: texte(couleur),
        border: `0.5px solid ${bordure(couleur)}`,
        borderRadius: 8,
        padding: 12,
        lineHeight: 1.6,
      }}
    >
      {titre && <div style={{ fontWeight: 500 }}>{titre}</div>}
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}

function Etiquette({ couleur = "blue", children }) {
  return (
    <span
      style={{
        background: fond(couleur),
        color: texte(couleur),
        borderRadius: 8,
        padding: "2px 8px",
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** La distribution des cinq. Toujours les cinq, même à zéro : une modalité
 *  absente du graphique se lirait « inexistante » au lieu de « jamais vue ». */
function Distribution({ donnees }) {
  const total = donnees?.total || 0;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {RESULTATS.map((r) => {
          const n = donnees?.par_resultat?.[r.cle] ?? 0;
          const pct = donnees?.pourcentages?.[r.cle] ?? 0;
          return (
            <div
              key={r.cle}
              style={{
                background: fond(r.couleur),
                color: texte(r.couleur),
                border: `0.5px solid ${bordure(r.couleur)}`,
                borderRadius: 8,
                padding: 12,
                minWidth: 132,
                flex: "1 1 132px",
              }}
            >
              <div style={{ fontSize: 22, lineHeight: 1.2 }}>{n}</div>
              <div style={{ fontSize: 13 }}>{r.label}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{pct} %</div>
            </div>
          );
        })}
      </div>
      {total > 0 && donnees?.constant && (
        <Encart couleur="red" titre="Distribution constante">
          {donnees.taux_dominant} % des verdicts valent « {donnees.dominant} ». Un
          indicateur qui ne discrimine pas ne mesure rien — c'est la forme exacte
          des 150 verdicts « incomplet » sur 155.
        </Encart>
      )}
      {total > 0 && !donnees?.constant && (
        <Encart couleur="green" titre="Indicateur discriminant">
          {donnees.modalites_observees} modalités observées sur 5, dominante à{" "}
          {donnees.taux_dominant} %.
        </Encart>
      )}
      {total === 0 && (
        <Encart couleur="blue">Aucun verdict sur la fenêtre choisie.</Encart>
      )}
    </div>
  );
}

function Vide({ children }) {
  return (
    <div style={{ color: "var(--oh-text-muted)", padding: 16, lineHeight: 1.6 }}>{children}</div>
  );
}

function Tableau({ colonnes, lignes, cle }) {
  if (!lignes?.length) return <Vide>Rien à afficher.</Vide>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {colonnes.map((c) => (
              <th
                key={c.cle}
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  color: "var(--oh-text-secondary)",
                  fontWeight: 500,
                  borderBottom: "0.5px solid var(--oh-border)",
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={cle ? l[cle] ?? i : i}>
              {colonnes.map((c) => (
                <td
                  style={{
                    padding: "8px 12px",
                    color: "var(--oh-text)",
                    borderBottom: "0.5px solid var(--oh-border)",
                    verticalAlign: "top",
                    lineHeight: 1.6,
                  }}
                  key={c.cle}
                >
                  {c.rendu ? c.rendu(l) : l[c.cle] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// 1 · DASHBOARD
// =============================================================================

function VueDashboard({ donnees, onVoirPoints }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const { agents, verdicts, evenements, findings_ouverts, anomalies } = donnees;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
        <Carte>
          <Titre aide="agents enregistrés">Agents</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>{agents?.total ?? 0}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {agents?.off?.length > 0 && (
              <Etiquette couleur="yellow">{agents.off.length} désactivé(s)</Etiquette>
            )}
            {agents?.en_erreur?.length > 0 && (
              <Etiquette couleur="red">{agents.en_erreur.length} en erreur</Etiquette>
            )}
          </div>
        </Carte>
        <Carte>
          <Titre aide="sur la fenêtre">Verdicts</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>{verdicts?.total ?? 0}</div>
        </Carte>
        <Carte>
          <button type="button" onClick={onVoirPoints} data-testid="hub-ia-carte-points"
                  aria-label={`Points à vérifier : ${findings_ouverts ?? 0}. Voir la liste`}
                  style={{ all: "unset", cursor: "pointer", display: "block", width: "100%" }}>
            <Titre aide="issus des verdicts non conformes · voir la liste">Points à vérifier</Titre>
            <div style={{ fontSize: 28, color: "var(--oh-accent-text)" }}>{findings_ouverts ?? 0}</div>
          </button>
        </Carte>
        <Carte>
          <Titre aide="reçus, personne n'écoute">Signaux morts</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>
            {evenements?.nb_signaux_morts ?? 0}
          </div>
        </Carte>
      </div>

      <Carte>
        <Titre aide="les cinq résultats — jamais deux">Distribution des verdicts</Titre>
        <Distribution donnees={verdicts} />
      </Carte>

      <Carte>
        <Titre aide="ce que le Hub signale sur lui-même">Anomalies</Titre>
        {anomalies?.length ? (
          <div style={{ display: "grid", gap: 8 }}>
            {anomalies.map((a) => (
              <Encart key={a.code} couleur="red" titre={a.code}>
                {a.message}
              </Encart>
            ))}
          </div>
        ) : (
          <Encart couleur="green" titre="Aucune anomalie">
            Distribution discriminante, aucun signal mort, sorties consommées.
          </Encart>
        )}
      </Carte>
    </div>
  );
}

// =============================================================================
// 2 · AGENTS (+ gouvernance)
// =============================================================================

function VueAgents({ donnees, onChangerMode, occupe, peutChanger }) {
  const [aEteindre, setAEteindre] = useState(null);
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {!peutChanger && (
        <Encart couleur="blue" titre="Lecture seule">
          Seul un super administrateur peut changer le mode d'un agent.
        </Encart>
      )}
      <AlertDialog open={aEteindre !== null} onOpenChange={(ouvert) => { if (!ouvert) setAEteindre(null); }}>
        <AlertDialogContent data-testid="hub-ia-confirmer-off"
                            style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
                                     borderColor: "var(--oh-border)", borderRadius: 12 }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Éteindre cet agent ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              {aEteindre?.libelle} ne s'exécutera plus pour cet espace. Les verdicts déjà rendus sont conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction data-testid="hub-ia-confirmer-off-ok"
                               onClick={() => onChangerMode(aEteindre.code, "off")}
                               style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              Éteindre l'agent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {donnees.agents?.map(({ descripteur, configuration, indicateurs, derniere_execution }) => (
        <Carte key={descripteur.code}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 260 }}>
              <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>
                {descripteur.libelle}
              </div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>
                {descripteur.code} · version {descripteur.version}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <Etiquette couleur="blue">{descripteur.famille}</Etiquette>
                <Etiquette couleur="cyan">{descripteur.nature}</Etiquette>
                <Etiquette couleur="purple">priorité {configuration.priorite}</Etiquette>
                {descripteur.en_conflit && (
                  <Etiquette couleur="red">version en conflit</Etiquette>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
              {MODES.map((m) => {
                const actif = configuration.mode === m.cle;
                return (
                  <button
                    key={m.cle}
                    type="button"
                    title={m.aide}
                    disabled={occupe || !peutChanger}
                    data-testid={`hub-ia-mode-${descripteur.code}-${m.cle}`}
                    onClick={() => (m.cle === "off"
                      ? setAEteindre({ code: descripteur.code, libelle: descripteur.libelle })
                      : onChangerMode(descripteur.code, m.cle))}
                    style={{
                      height: 36,
                      padding: "0 12px",
                      borderRadius: 8,
                      cursor: occupe || !peutChanger ? "not-allowed" : "pointer",
                      background: actif ? "var(--oh-accent-solid)" : "transparent",
                      color: actif ? "var(--oh-accent-contrast)" : "var(--oh-text-secondary)",
                      border: `1px solid ${actif ? "var(--oh-accent-solid)" : "var(--oh-border)"}`,
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {descripteur.en_conflit && (
            <div style={{ marginTop: 12 }}>
              <Encart couleur="red" titre="L'agent refuse de s'exécuter">
                {descripteur.conflit}
              </Encart>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
              marginTop: 16,
            }}
          >
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Activité</div>
              <div style={{ color: "var(--oh-text)" }}>
                {indicateurs.activite.executions} exécutions ·{" "}
                {indicateurs.activite.non_executions} refusées
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                {indicateurs.activite.duree_moyenne_ms != null
                  ? `${indicateurs.activite.duree_moyenne_ms} ms en moyenne`
                  : "aucune durée mesurée"}
              </div>
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Qualité</div>
              <div style={{ color: "var(--oh-text)" }}>
                {indicateurs.qualite.taux_conclusion} % de conclusion
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                {indicateurs.qualite.taux_indetermination} % indéterminés ·{" "}
                {indicateurs.qualite.taux_erreur_technique} % erreurs techniques
              </div>
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Dernière exécution</div>
              <div style={{ color: "var(--oh-text)", fontSize: 13 }}>
                {derniere_execution?.horodatage
                  ? new Date(derniere_execution.horodatage).toLocaleString("fr-FR")
                  : "jamais"}
              </div>
              {derniere_execution && !derniere_execution.execute && (
                <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                  {derniere_execution.raison_non_execution}
                </div>
              )}
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Sortie</div>
              <div style={{ color: "var(--oh-text)", fontSize: 13 }}>
                {descripteur.evenement_publie || "aucun événement"}
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>
                consommée par {descripteur.consommateur}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <Distribution donnees={{
              total: indicateurs.resultats.verdicts,
              par_resultat: indicateurs.resultats.distribution,
              pourcentages: indicateurs.resultats.pourcentages,
              constant: indicateurs.qualite.constant,
              modalites_observees: indicateurs.qualite.modalites_observees,
            }} />
          </div>

          {configuration.mode === "off" && (
            <div style={{ marginTop: 12 }}>
              <Encart couleur="yellow" titre="Agent désactivé">
                Il ne s'exécutera dans aucun des quatre modes. Ses verdicts passés
                restent lisibles et datés — désactiver n'est pas supprimer.
              </Encart>
            </div>
          )}
        </Carte>
      ))}
    </div>
  );
}

// =============================================================================
// 3 · CAPACITÉS
// =============================================================================

function VueCapacites({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <Carte>
      <Titre aide="ce que le Hub sait faire — et qui s'en sert d'après l'exécution, pas d'après la déclaration">
        Capacités ({donnees.total})
      </Titre>
      {donnees.declarees_jamais_utilisees?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Encart couleur="yellow" titre="Déclarées mais jamais appelées">
            {donnees.declarees_jamais_utilisees.join(" · ")} — une capacité déclarée
            requise et jamais exécutée est une déclaration, pas un usage.
          </Encart>
        </div>
      )}
      <Tableau
        cle="code"
        lignes={donnees.capacites}
        colonnes={[
          { cle: "code", label: "Capacité", rendu: (c) => (
            <div>
              <div style={{ color: "var(--oh-text)" }}>{c.libelle}</div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>{c.code} · v{c.version}</div>
            </div>
          ) },
          { cle: "domaine", label: "Domaine", rendu: (c) => <Etiquette couleur="cyan">{c.domaine}</Etiquette> },
          { cle: "fournisseur", label: "Fournisseur unique", rendu: (c) => (
            <span style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>{c.fournisseur}</span>
          ) },
          { cle: "declaree_par", label: "Déclarée par", rendu: (c) => c.declaree_par?.join(", ") || "—" },
          { cle: "appels", label: "Appels réels", rendu: (c) => (
            c.appels > 0
              ? <Etiquette couleur="green">{c.appels}</Etiquette>
              : <Etiquette couleur="yellow">0</Etiquette>
          ) },
        ]}
      />
    </Carte>
  );
}

// =============================================================================
// 4 · ÉVÉNEMENTS
// =============================================================================

function VueEvenements({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {donnees.nb_signaux_morts > 0 ? (
        <Encart couleur="red" titre={`${donnees.nb_signaux_morts} signal(aux) mort(s)`}>
          Ces événements sont REÇUS et personne ne les écoute. C'est SMELL-05 :
          un producteur, zéro destinataire. Précédent — « document.persiste » a eu
          1 producteur et 0 abonné pendant plusieurs heures, sans que rien ne le dise.
        </Encart>
      ) : (
        <Encart couleur="green" titre="Aucun signal mort">
          Tout événement reçu a au moins un abonné.
        </Encart>
      )}
      <Carte>
        <Titre aide="flux, producteurs, abonnés — et ce que personne n'écoute">
          Flux d'événements
        </Titre>
        <Tableau
          cle="event_type"
          lignes={donnees.evenements}
          colonnes={[
            { cle: "event_type", label: "Événement" },
            { cle: "volume", label: "Reçus" },
            { cle: "handlers_bus", label: "Abonnés bus" },
            { cle: "agents_abonnes", label: "Agents", rendu: (e) => e.agents_abonnes?.join(", ") || "—" },
            { cle: "etat", label: "État", rendu: (e) =>
              e.signal_mort ? <Etiquette couleur="red">signal mort</Etiquette>
              : e.sans_abonne ? <Etiquette couleur="yellow">sans abonné</Etiquette>
              : <Etiquette couleur="green">consommé</Etiquette> },
            { cle: "dernier", label: "Dernier", rendu: (e) =>
              e.dernier ? new Date(e.dernier).toLocaleString("fr-FR") : "—" },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 5 · VERDICTS
// =============================================================================

function VueVerdicts({ donnees, filtre, onFiltrer }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const couleurDe = (cle) => RESULTATS.find((r) => r.cle === cle)?.couleur || "blue";
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Carte>
        <Titre aide="la distribution des cinq — l'indicateur qui aurait signalé le 97 % constant">
          Distribution
        </Titre>
        <Distribution donnees={donnees.distribution} />
      </Carte>

      <Carte>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => onFiltrer(null)}
            style={{
              height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
              background: !filtre ? "var(--oh-accent-bg)" : "transparent",
              color: !filtre ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
              border: "1px solid var(--oh-border)",
            }}
          >
            Tous
          </button>
          {RESULTATS.map((r) => (
            <button
              key={r.cle}
              type="button"
              onClick={() => onFiltrer(r.cle)}
              style={{
                height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
                background: filtre === r.cle ? fond(r.couleur) : "transparent",
                color: filtre === r.cle ? texte(r.couleur) : "var(--oh-text-secondary)",
                border: `1px solid ${filtre === r.cle ? bordure(r.couleur) : "var(--oh-border)"}`,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <Tableau
          cle="verdict_id"
          lignes={donnees.verdicts}
          colonnes={[
            { cle: "resultat", label: "Résultat", rendu: (v) => (
              <Etiquette couleur={couleurDe(v.resultat)}>
                {RESULTATS.find((r) => r.cle === v.resultat)?.label || v.resultat}
              </Etiquette>
            ) },
            { cle: "regle_code", label: "Règle", rendu: (v) => (
              <div>
                <div style={{ color: "var(--oh-text)" }}>{v.regle_code}</div>
                <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
                  v{v.regle_version} · {v.agent_code} v{v.agent_version}
                </div>
              </div>
            ) },
            { cle: "cible_id", label: "Objet", rendu: (v) => (
              <span style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>
                {v.cible_type} · {v.cible_id}
              </span>
            ) },
            { cle: "justification", label: "Justification", rendu: (v) => (
              <div style={{ maxWidth: 420 }}>
                <div>{v.justification}</div>
                {v.donnees_evaluees && Object.keys(v.donnees_evaluees).length > 0 && (
                  <div style={{ color: "var(--oh-text-muted)", fontSize: 12, marginTop: 4 }}>
                    lu : {Object.keys(v.donnees_evaluees).join(", ")}
                  </div>
                )}
              </div>
            ) },
            { cle: "horodatage", label: "Quand", rendu: (v) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {new Date(v.horodatage).toLocaleString("fr-FR")}
              </span>
            ) },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 6 · RÈGLES
// =============================================================================

function VueRegles({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <Carte>
      <Titre aide="ajouter un contrôle est une entrée de référentiel, jamais une ligne de code. Une version publiée ne se modifie jamais.">
        Référentiel ({donnees.total}) · domaines : {donnees.domaines?.join(", ") || "—"}
      </Titre>
      <Tableau
        lignes={donnees.regles}
        colonnes={[
          { cle: "code", label: "Règle", rendu: (r) => (
            <div>
              <div style={{ color: "var(--oh-text)" }}>{r.libelle}</div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
                {r.code} · v{r.version} · {r.domaine}
              </div>
            </div>
          ) },
          { cle: "tenant_id", label: "Portée", rendu: (r) =>
            r.tenant_id === "*"
              ? <Etiquette couleur="blue">mutualisée</Etiquette>
              : <Etiquette couleur="purple">surcharge {r.tenant_id}</Etiquette> },
          { cle: "validite", label: "Validité", rendu: (r) => (
            <span style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
              {r.date_debut_validite || "—"} → {r.date_fin_validite || "sans fin"}
            </span>
          ) },
          { cle: "en_vigueur", label: "État", rendu: (r) =>
            r.en_vigueur
              ? <Etiquette couleur="green">en vigueur</Etiquette>
              : <Etiquette couleur="yellow">hors vigueur</Etiquette> },
          { cle: "gravite", label: "Gravité" },
          { cle: "application", label: "Domaine d'application", rendu: (r) => (
            <code style={{ fontSize: 11, color: "var(--oh-text-secondary)" }}>
              {JSON.stringify(r.application || {})}
            </code>
          ) },
        ]}
      />
    </Carte>
  );
}

// =============================================================================
// 7 · MONITORING
// =============================================================================

function VueMonitoring({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const t = donnees.temps_execution_ms || {};
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
        <Carte>
          <Titre aide="médiane · p90 · max">Temps d'exécution</Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>{t.median ?? "—"} ms</div>
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
            p90 {t.p90 ?? "—"} ms · max {t.max ?? "—"} ms
          </div>
        </Carte>
        <Carte>
          <Titre aide="défauts du Hub, pas des dossiers">Erreurs techniques</Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>
            {donnees.taux_erreur_technique} %
          </div>
        </Carte>
        <Carte>
          <Titre aide="runs batch interrompus, repris au dernier objet traité">
            Files en attente
          </Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>
            {donnees.nb_files_en_attente}
          </div>
        </Carte>
      </div>
      <Carte>
        <Titre aide="reprise exacte : le curseur est le dernier identifiant traité">
          Runs batch reprenables
        </Titre>
        <Tableau
          cle="run_id"
          lignes={donnees.files_en_attente}
          colonnes={[
            { cle: "agent_code", label: "Agent" },
            { cle: "statut", label: "Statut" },
            { cle: "traites", label: "Traités" },
            { cle: "curseur", label: "Curseur" },
            { cle: "run_id", label: "Run" },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 8 · QUALITÉ DES DONNÉES — AID-OPP-02 déclinée (#370)
// =============================================================================

function VueQualite({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const f = donnees.formule_priorite || {};
  const a = donnees.actionnable_maintenant || {};
  const ind = donnees.non_actionnable_encore || {};
  const max = Math.max(1, ...(donnees.domaines || []).map((d) => d.impact || 0));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Ce qui est actionnable SANS saisir une seule donnée passe en premier. */}
      <Carte style={{ borderColor: bordure("amber") }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ background: fond("amber"), color: texte("amber"),
                        borderRadius: 8, padding: 8, display: "flex" }}>
            <Sparkles size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>
              {a.opportunites_certaines ?? 0} opportunités certaines sur{" "}
              {a.dossiers_avec_opportunite ?? 0} dossiers
            </div>
            <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
              Actionnables immédiatement — aucune donnée à compléter. À traiter
              avant tout domaine ci-dessous.
            </div>
          </div>
        </div>
      </Carte>

      <Carte>
        <Titre aide="indicateur de qualité de données — pas un indicateur de performance opérateur">
          Domaines à compléter ({donnees.dossiers_examines} dossiers examinés)
        </Titre>

        {/* ⚠️ LA FORMULE, À CÔTÉ DU CLASSEMENT. */}
        <div style={{ marginBottom: 12 }}>
          <Encart couleur="blue" titre="Comment ce classement est calculé">
            <div>{f.explication}</div>
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: "pointer" }}>Détails techniques</summary>
              <code style={{ fontSize: 12 }}>{f.expression}</code>
            </details>
          </Encart>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {(donnees.domaines || []).map((d) => (
            <div key={d.champ} style={{
              border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            gap: 12, flexWrap: "wrap" }}>
                <span style={{ color: "var(--oh-text)", lineHeight: 1.6 }}>{d.libelle}</span>
                <span style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                  {d.dossiers_sans_la_donnee} dossiers ({d.part} %) × {d.nb_aides_debloquees} aide(s)
                  {" = "}
                  <strong style={{ color: "var(--oh-accent-text)" }}>impact {d.impact}</strong>
                </span>
              </div>
              <div style={{ marginTop: 8, height: 6, borderRadius: 8,
                            background: "var(--oh-bg)", overflow: "hidden" }}>
                <div style={{ width: `${Math.round(100 * (d.impact || 0) / max)}%`,
                              height: "100%", background: "var(--oh-accent-solid)" }} />
              </div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12, marginTop: 6 }}>
                débloquerait : {(d.aides_dependantes || []).join(" · ")}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12 }}>
          {/* Le double compte est dit, pas caché. */}
          <Encart couleur="slate" titre={`${donnees.dossiers_incomplets_distincts} dossiers incomplets (effectif distinct)`}>
            {donnees.explication_double_compte}
          </Encart>
        </div>
      </Carte>

      {/* ⚠️ RANG 3 — SÉPARÉ DES DOMAINES, ET APRÈS EUX.
          Un domaine dit « quelle donnée compléter » ; une indétermination dit
          « sur quelle aide on ne peut pas encore conclure ». Les mêler ferait
          lire une absence de savoir comme un manquement du dossier. */}
      <Carte>
        <Titre aide="ni actionnable aujourd'hui, ni un manquement du dossier">
          Situations non encore actionnables
        </Titre>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ background: fond("blue"), color: texte("blue"),
                        border: `0.5px solid ${bordure("blue")}`,
                        borderRadius: 8, padding: 12, minWidth: 160, flex: "1 1 160px" }}>
            <div style={{ fontSize: 22, lineHeight: 1.2 }}>
              {ind.aides_indeterminees ?? 0}
            </div>
            <div style={{ fontSize: 13 }}>aides à l'éligibilité indéterminée</div>
          </div>
          <div style={{ background: fond("blue"), color: texte("blue"),
                        border: `0.5px solid ${bordure("blue")}`,
                        borderRadius: 8, padding: 12, minWidth: 160, flex: "1 1 160px" }}>
            <div style={{ fontSize: 22, lineHeight: 1.2 }}>
              {ind.dossiers_avec_indetermination ?? 0}
            </div>
            <div style={{ fontSize: 13 }}>dossiers concernés</div>
          </div>
        </div>
      </Carte>
    </div>
  );
}


// =============================================================================
// POINTS À VÉRIFIER (T-431 / PN-18) — les constats ouverts, que l'on peut traiter
// =============================================================================

const GRAVITES = {
  CRITIQUE: { label: "Critique", couleur: "red" }, GRAVE: { label: "Grave", couleur: "orange" },
  MODERE: { label: "Modéré", couleur: "yellow" }, MINEUR: { label: "Mineur", couleur: "blue" },
};

function VuePoints({ donnees, peutTraiter, onTraiter, onOuvrirDossier, occupe }) {
  const [demande, setDemande] = useState(null); // { reference, sens, titre }
  const [motif, setMotif] = useState("");
  if (!donnees) return <Vide>Chargement…</Vide>;
  // T-431b : tri sur la référence lue côté serveur ; « Dossier introuvable » après les dossiers connus, « Sans dossier » en fin de liste.
  const rang = (f) => (f.dossier_ref ? 0 : f.dossier_id ? 1 : 2);
  const lignes = [...(donnees.findings || [])].sort((a, b) =>
    rang(a) - rang(b) || String(a.dossier_ref || "").localeCompare(String(b.dossier_ref || ""), "fr", { numeric: true }));
  const fermer = () => { setDemande(null); setMotif(""); };
  const valider = async () => {
    const ok = await onTraiter(demande.reference, demande.sens, motif.trim());
    if (ok) fermer();
  };
  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="hub-ia-points">
      <Encart couleur="blue" titre={`${lignes.length} point${lignes.length > 1 ? "s" : ""} à vérifier`}>
        Chaque point vient d'un contrôle qui a trouvé un écart sur un dossier. Résolvez-le, ou mettez-le de côté en disant pourquoi ; il se ferme aussi tout seul quand le contrôle suivant est conforme.
      </Encart>
      {lignes.length === 0 && <Vide>Aucun point à vérifier.</Vide>}
      {lignes.map((f) => {
        const g = GRAVITES[f.gravite] || { label: f.gravite || "—", couleur: "blue" };
        const dossier = f.dossier_id;
        return (
          <Carte key={f.reference}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 240, flex: "1 1 240px" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <Etiquette couleur={g.couleur}>{g.label}</Etiquette>
                  {dossier && f.dossier_ref && (
                    <button type="button" onClick={() => onOuvrirDossier?.(dossier)} disabled={!onOuvrirDossier}
                            data-testid={`hub-ia-point-dossier-${f.reference}`}
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0,
                                     color: "var(--oh-accent-text)", textDecoration: "underline" }}>
                      Dossier {f.dossier_ref}
                    </button>
                  )}
                  {dossier && !f.dossier_ref && (
                    <span data-testid={`hub-ia-point-introuvable-${f.reference}`} style={{ color: "var(--oh-text-muted)" }}>
                      Dossier introuvable
                    </span>
                  )}
                  {!dossier && (
                    <span data-testid={`hub-ia-point-sans-dossier-${f.reference}`} style={{ color: "var(--oh-text-muted)" }}>
                      Sans dossier
                    </span>
                  )}
                </div>
                <div style={{ color: "var(--oh-text)", marginTop: 8, lineHeight: 1.6 }}>{f.title || f.description || "—"}</div>
                {f.action_recommandee && (
                  <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>À faire : {f.action_recommandee}</div>
                )}
              </div>
              {peutTraiter && (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                  {[["resoudre", "Résoudre", "secondaire"], ["ignorer", "Mettre de côté", "discret"]].map(([sens, texte, type]) => (
                    <button key={sens} type="button" disabled={occupe}
                            data-testid={`hub-ia-point-${sens}-${f.reference}`}
                            onClick={() => setDemande({ reference: f.reference, sens, titre: f.title })}
                            style={{ height: 36, padding: "0 12px", borderRadius: 8, background: "transparent",
                                     cursor: occupe ? "not-allowed" : "pointer",
                                     color: type === "secondaire" ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                                     border: `1px solid ${type === "secondaire" ? "var(--oh-accent)" : "var(--oh-border)"}` }}>
                      {texte}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <details style={{ marginTop: 8, color: "var(--oh-text-muted)", fontSize: 13 }}>
              <summary style={{ cursor: "pointer" }}>Détails techniques</summary>
              {f.reference} · règle {f.regle} · agent {f.agent_code}
            </details>
          </Carte>
        );
      })}
      <AlertDialog open={demande !== null} onOpenChange={(ouvert) => { if (!ouvert) fermer(); }}>
        <AlertDialogContent data-testid="hub-ia-point-dialogue"
                            style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-border)", borderRadius: 12 }}>
          <AlertDialogHeader>
            <AlertDialogTitle>{demande?.sens === "resoudre" ? "Résoudre ce point ?" : "Mettre ce point de côté ?"}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              {demande?.titre}. Dites pourquoi : le motif est conservé avec votre nom.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={500}
                    aria-label="Motif" placeholder="Motif (obligatoire)" data-testid="hub-ia-point-motif"
                    style={{ width: "100%", borderRadius: 8, padding: 8, background: "var(--oh-bg)", color: "var(--oh-text)",
                             border: "1px solid var(--oh-border)" }} />
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction data-testid="hub-ia-point-valider" disabled={!motif.trim() || occupe}
                               onClick={(e) => { e.preventDefault(); valider(); }}
                               style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              {demande?.sens === "resoudre" ? "Résoudre" : "Mettre de côté"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// =============================================================================
// L'ÉCRAN
// =============================================================================

// Une seule période pour tout l'écran, dite en clair (T-431 / PN-20).
const PERIODE_HEURES = 720;
const PERIODE_LIBELLE = "30 derniers jours";

// Chaque vue lit ses propres routes : une route en panne n'abîme que sa vue (fin du Promise.all).
const CHARGEURS = {
  dashboard: async () => appeler(`/dashboard?heures=${PERIODE_HEURES}`),
  agents: async () => appeler(`/agents?heures=${PERIODE_HEURES}`),
  capacites: async () => appeler(`/capacites?heures=${PERIODE_HEURES}`),
  evenements: async () => appeler(`/evenements?heures=${PERIODE_HEURES}`),
  verdicts: async ({ filtreResultat }) => {
    const depuis = new Date(Date.now() - PERIODE_HEURES * 3600 * 1000).toISOString();
    const q = new URLSearchParams({ depuis, ...(filtreResultat ? { resultat: filtreResultat } : {}) });
    return appeler(`/verdicts?${q}`);
  },
  regles: async () => appeler("/regles"),
  monitoring: async () => appeler(`/monitoring?heures=${PERIODE_HEURES}`),
  qualite: async () => appeler("/qualite-donnees"),
  points: async () => appeler("/findings?statut=ouvert&limite=200"),
};

// Deux onglets pour l'admin du tenant ; le reste, réservé au super admin, est rangé dans « Outils avancés ».
const ONGLETS = [
  { id: "points", label: "Points à vérifier", icon: AlertTriangle },
  { id: "qualite", label: "Qualité des données", icon: ClipboardList },
  { id: "avances", label: "Outils avancés", icon: Layers, superAdmin: true },
];
const VUES_AVANCEES = VUES.filter((v) => !["points", "qualite"].includes(v.id));

export default function HubIA({ onClose, onOpenDossier }) {
  const [onglet, setOnglet] = useState("points");
  const [sousVue, setSousVue] = useState("dashboard");
  const [donnees, setDonnees] = useState({});
  const [erreurs, setErreurs] = useState({});
  const [chargements, setChargements] = useState({});
  const [filtreResultat, setFiltreResultat] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [erreurAction, setErreurAction] = useState(null);
  const permissions = useContext(PermissionContext);
  const peutChanger = Boolean(permissions?.isSuperAdmin?.());
  const peutTraiter = Boolean(permissions?.isAdmin?.());
  const vue = onglet === "avances" && peutChanger ? sousVue : onglet === "avances" ? "points" : onglet;

  const chargerVue = useCallback(async (id) => {
    setChargements((c) => ({ ...c, [id]: true }));
    setErreurs((e) => ({ ...e, [id]: null }));
    try {
      const valeur = await CHARGEURS[id]({ filtreResultat });
      setDonnees((d) => ({ ...d, [id]: valeur }));
    } catch (e) {
      setErreurs((er) => ({ ...er, [id]: e.message || "Erreur de chargement" }));
    } finally {
      setChargements((c) => ({ ...c, [id]: false }));
    }
  }, [filtreResultat]);

  // Le compteur de l'onglet « Points à vérifier » se charge dès l'ouverture, indépendamment de l'onglet affiché.
  useEffect(() => { chargerVue("points"); }, [chargerVue]);
  useEffect(() => { if (vue !== "points") chargerVue(vue); }, [vue, chargerVue]);

  const charger = useCallback(() => {
    chargerVue(vue);
    if (vue !== "points") chargerVue("points");
  }, [chargerVue, vue]);

  const changerMode = useCallback(
    async (agentCode, mode) => {
      setOccupe(true);
      try {
        await appeler(`/agents/${agentCode}/gouvernance`, {
          method: "PUT",
          body: JSON.stringify({ mode }), // l'auteur est l'utilisateur de la session, posé par le serveur
        });
        await chargerVue("agents");
      } catch (e) {
        setErreurAction(e.message);
      } finally {
        setOccupe(false);
      }
    },
    [chargerVue]
  );

  const traiterPoint = useCallback(
    async (reference, sens, motif) => {
      setOccupe(true);
      try {
        await appeler(`/findings/${encodeURIComponent(reference)}/${sens}`, {
          method: "POST",
          body: JSON.stringify({ motif }),
        });
        await chargerVue("points"); // la liste et le compteur de l'onglet viennent de la même lecture
        setDonnees((d) => ({ ...d, dashboard: undefined })); // le Dashboard (Outils avancés) se relira à l'ouverture
        return true;
      } catch (e) {
        setErreurAction(e.message);
        return false;
      } finally {
        setOccupe(false);
      }
    },
    [chargerVue]
  );

  const contenu = useMemo(() => {
    switch (vue) {
      case "agents":
        return <VueAgents donnees={donnees.agents} onChangerMode={changerMode} occupe={occupe} peutChanger={peutChanger} />;
      case "capacites":
        return <VueCapacites donnees={donnees.capacites} />;
      case "evenements":
        return <VueEvenements donnees={donnees.evenements} />;
      case "verdicts":
        return (
          <VueVerdicts
            donnees={donnees.verdicts}
            filtre={filtreResultat}
            onFiltrer={setFiltreResultat}
          />
        );
      case "regles":
        return <VueRegles donnees={donnees.regles} />;
      case "monitoring":
        return <VueMonitoring donnees={donnees.monitoring} />;
      case "qualite":
        return <VueQualite donnees={donnees.qualite} />;
      case "points":
        return (
          <VuePoints donnees={donnees.points} peutTraiter={peutTraiter} onTraiter={traiterPoint}
                     onOuvrirDossier={onOpenDossier} occupe={occupe} />
        );
      default:
        return <VueDashboard donnees={donnees.dashboard} onVoirPoints={() => setOnglet("points")} />;
    }
  }, [vue, donnees, filtreResultat, changerMode, occupe, peutChanger, peutTraiter, traiterPoint, onOpenDossier]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--oh-bg)" }}>
      <div
        style={{
          borderBottom: "0.5px solid var(--oh-border)",
          background: "var(--oh-bg-surface)",
          padding: "16px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Revenir à l'accueil"
              style={{
                height: 36, width: 36, borderRadius: 8, cursor: "pointer",
                background: "transparent", color: "var(--oh-text-secondary)",
                border: "1px solid var(--oh-border)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <Zap size={20} style={{ color: "var(--oh-accent-text)" }} />
          <div>
            <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>Hub IA</div>
            <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>
              Points à vérifier et qualité des données
            </div>
          </div>
          <button
            type="button"
            onClick={charger}
            disabled={Boolean(chargements[vue])}
            aria-label="Rafraîchir les données"
            style={{
              marginLeft: "auto", height: 36, padding: "0 12px", borderRadius: 8,
              cursor: chargements[vue] ? "not-allowed" : "pointer",
              background: "transparent", color: "var(--oh-text-secondary)",
              border: "1px solid var(--oh-border)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <RefreshCw size={14} /> Rafraîchir
          </button>
        </div>

        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13, lineHeight: 1.6, marginTop: 12 }}
             data-testid="hub-ia-phrase">
          Ce que les agents ont relevé sur vos dossiers, sur les {PERIODE_LIBELLE}, et ce qu'il reste à vérifier.
        </div>

        <div role="tablist" style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          {ONGLETS.filter((o) => !o.superAdmin || peutChanger).map((o) => {
            const Icone = o.icon;
            const actif = onglet === o.id;
            const n = o.id === "points" ? donnees.points?.total : null;
            return (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={actif}
                data-testid={`hub-ia-onglet-${o.id}`}
                onClick={() => setOnglet(o.id)}
                style={{
                  height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8,
                  background: actif ? "var(--oh-accent-bg)" : "transparent",
                  color: actif ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                  border: `1px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                }}
              >
                <Icone size={14} /> {o.label}{n != null ? ` (${n})` : ""}
              </button>
            );
          })}
        </div>

        {onglet === "avances" && peutChanger && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }} data-testid="hub-ia-avances">
            {VUES_AVANCEES.map((v) => {
              const Icone = v.icon;
              const actif = sousVue === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`hub-ia-vue-${v.id}`}
                  onClick={() => setSousVue(v.id)}
                  style={{
                    height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                    background: actif ? "var(--oh-accent-bg)" : "transparent",
                    color: actif ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                    border: `1px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                  }}
                >
                  <Icone size={14} /> {v.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding: 24 }}>
        {(erreurAction || erreurs[vue]) && (
          <div style={{ marginBottom: 16 }} data-testid={`hub-ia-erreur-${vue}`}>
            <Encart couleur="red" titre={erreurs[vue] ? "Cette partie ne se charge pas" : "Action impossible"}>
              {erreurs[vue] || erreurAction}
            </Encart>
          </div>
        )}
        {erreurs[vue] && !donnees[vue] ? null : contenu}
      </div>
    </div>
  );
}
